/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { v1 as uuidv1 } from 'uuid';
import { createSelector } from 'reselect';
import { Box, Table } from '@material-ui/core';

import * as c from 'constants/sales';
import { PosPlugin } from 'plugins/plugin';
import { getClientCode } from 'reducers/Login';
import { openModalPage } from 'actions/modalPage';
import { modalPages } from 'constants/modalPage';
import { getSetting } from 'reducers/configs/settings';
import { addError, addWarning } from 'actions/Error';
import { setDocSelected } from 'actions/returnProducts';
import { getCurrentSalesDocument } from 'reducers/sales';
import { ErplyAttributes } from 'utils';
import { SaleDocumentResponse } from 'types/SalesDocument';

import CFRReducer, {
  toggleIsOnlineReturn,
  toggleIsCFRReturn,
} from './rdx/index';
import getFranchiseSalesDocuments from './requests';
import { getCFRState, getISCFRReturn } from './rdx/reducers';

interface CFRParams {
  number?: string;
  nonReturnedItemsOnly: 1 | 0;
  types: string;
}

const retrieveDocForFranchiseReturn = async (params: CFRParams) =>
  getFranchiseSalesDocuments({
    ...params,
  });

const CrossFranchiseReturn: PosPlugin = {
  id: 'CFR',
  name: 'Cross franchise return',
  keywords: ['cross', 'franchise', 'return'],
  getStatus: () => {
    return {
      type: 'warning',
      message:
        'Status will be validated before performing a return transaction',
    };
  },
  info:
    'This plugin is used to process returns accross multiple locations within a franchise structure account.',
  reduxReducer: CFRReducer as PosPlugin['reduxReducer'],
  UIExchangeModalTitle: () => {
    const { t } = useTranslation('gridButtons');
    return (
      <Box fontSize="1.75rem" fontWeight={600} data-testid="return-title">
        {t('actions.crossFranchiseReturn')}
      </Box>
    );
  },
  onFetchSalesDocsForReturn: {
    on: (params, fetchedDocuments) => async dispatch => {
      if (fetchedDocuments.payload?.length) {
        if (
          fetchedDocuments.payload.every(doc => doc.paymentType === 'ONLINE')
        ) {
          dispatch(toggleIsOnlineReturn(true));
        }
        return fetchedDocuments;
      }

      const retrievedCFRDocs = await retrieveDocForFranchiseReturn({
        number: params.number,
        nonReturnedItemsOnly: 1,
        types: 'INVWAYBILL,CASHINVOICE,EXPORTINVOICE,INVOICE',
      });

      const documentsWithOnlinePayments = retrievedCFRDocs
        .map(doc => {
          if (doc.payments.every(payment => payment.type === 'ONLINE')) {
            return doc;
          }
          return undefined;
        })
        .filter(doc => doc !== undefined);

      const nonReturnedRows = retrievedCFRDocs.length
        ? retrievedCFRDocs[0].rows
        : [];

      if (nonReturnedRows.length) {
        dispatch(toggleIsCFRReturn(true));
      }

      if (documentsWithOnlinePayments.length) {
        dispatch(toggleIsOnlineReturn(true));
      }

      const defineReturnPayload = () => {
        if (fetchedDocuments.payload.length) {
          return fetchedDocuments;
        }

        if (retrievedCFRDocs.length) {
          const updatedCFRDocs = retrievedCFRDocs.map(doc => {
            // paid property needs to be added to the CFR document as it is not returned from the API and it is needed to display the total amount of payments when making the return
            return { ...doc, paid: doc.total };
          });

          return {
            returnedRows: nonReturnedRows,
            payload: updatedCFRDocs,
          };
        }

        return {
          returnedRows: [],
          payload: [],
        };
      };

      // temporary cast this as any and rewrite later at some point
      return defineReturnPayload() as any;
    },
  },
  selectorOverrides: {
    getFunctionButtons: base =>
      createSelector(
        base,
        getSetting('db_idFormat'),
        (baseButtons, DBFormat) => [
          ...baseButtons,
          {
            id: uuidv1(),
            name: 'Online Return',
            actionType: 'action',
            action: async dispatch => {
              if (DBFormat === '%012d') {
                retrieveDocForFranchiseReturn({
                  nonReturnedItemsOnly: 1,
                  types: 'INVWAYBILL,CASHINVOICE,EXPORTINVOICE,INVOICE',
                })
                  .then(() => {
                    return dispatch(
                      openModalPage({
                        component: modalPages.ProductReturn,
                        modalClassName: 'product-return-modal',
                      }),
                    );
                  })
                  .catch(e => {
                    if (e.errorCode === 1067) {
                      return dispatch(
                        addError(
                          'Unable to make cross franchise return - BO not configured',
                          {
                            selfDismiss: true,
                            dismissible: false,
                          },
                        ),
                      );
                    }

                    return dispatch(
                      openModalPage({
                        component: modalPages.ProductReturn,
                        modalClassName: 'product-return-modal',
                      }),
                    );
                  });
              } else {
                dispatch(
                  addError(
                    'Unable to make cross franchise return - wrong dbFormat',
                    {
                      selfDismiss: true,
                      dismissible: false,
                    },
                  ),
                );
              }
            },
          },
        ],
      ),
  },
  UIReturnOrderProduct: ({ row, children }) => {
    const defineRowToDisplay = () => {
      if (
        Number(row.amount) === 0 ||
        row.itemName?.toUpperCase() === 'FREIGHT0000'
      ) {
        return null;
      }

      return children;
    };

    return <>{defineRowToDisplay()}</>;
  },
  UIReturnTableExtraData: ({ returnDocument }) => {
    const { t } = useTranslation('return');
    const showLocation = !!(returnDocument as SaleDocumentResponse)
      .warehouseName;
    return (
      <Table>
        <thead>
          <tr>
            <th className="align-middle">{t('headers.number')}</th>
            <th className="align-middle">{t('headers.date')}</th>
            {showLocation && (
              <th className="align-middle">{t('headers.location')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td data-testid="document-number">{returnDocument.number}</td>
            <td data-testid="document-date">{returnDocument.date}</td>
            {showLocation && (
              <td data-testid="document-location">
                {(returnDocument as SaleDocumentResponse).warehouseName}
              </td>
            )}
          </tr>
        </tbody>
      </Table>
    );
  },
  UISalesDocumentSelection: ({ salesDocs, docSelected, beautifyType }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation('payment');

    return (
      <Table className="documents">
        <thead>
          <tr>
            <th>{t('number')}</th>
            <th>{t('customer')}</th>
            <th>{t('type')}</th>
            <th>{t('location')}</th>
            <th>{t('date')}</th>
            <th>{t('sum')}</th>
          </tr>
        </thead>
        <tbody>
          {salesDocs.map(document => (
            <tr
              key={document.id}
              className={docSelected === document.id ? 'selected' : undefined}
              data-testid="document-row"
              data-test-key={document.id}
              onClick={() => {
                dispatch(setDocSelected(document.id));
              }}
            >
              <td data-testid="document-number" data-test-key={document.number}>
                {document.number}
              </td>
              <td data-testid="client-name" data-test-key={document.clientName}>
                {document.clientName}
              </td>
              <td data-testid="document-type" data-test-key={document.type}>
                {beautifyType(document.type)}
              </td>
              <td
                data-testid="document-location"
                data-test-key={document.warehouseName}
              >
                {document.warehouseName}
              </td>
              <td data-testid="document-date" data-test-key={document.date}>
                {document.date}
              </td>
              <td data-testid="document-total" data-test-key={document.total}>
                {parseFloat(document.total).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  },
  onAddProduct: {
    on: (p, ap) => async (dispatch, getState) => {
      const isCFRReturn = getISCFRReturn(getState());

      if (isCFRReturn && ap.product.amount > 0) {
        dispatch(
          addWarning('Not allowed to have positive rows on return document', {
            dismissible: false,
            selfDismiss: true,
          }),
        );
        throw new Error('Not allowed to have positive rows on return document');
      }

      return ap;
    },
  },
  onSetCurrentSalesDocPayments: {
    before: () => async (dispatch, getState) => {
      const isCFRReturn = getISCFRReturn(getState());
      const currentSalesDoc = getCurrentSalesDocument(getState());

      if (isCFRReturn) {
        const saleDocPayments = currentSalesDoc.payments.filter(
          pmt => pmt.type !== 'GIFTCARD' || !pmt.attributes?.giftCardID,
        );

        dispatch({
          type: c.SET_CURRENT_SALES_DOC_PAYMENTS.SUCCESS,
          payload: { original: saleDocPayments, returned: [] },
        });

        throw new Error(
          'Stop the execution of onSetCurrentSalesDocPayments hook',
        );
      }
    },
  },
  onSaveSalesDocument: {
    on: (p, requests) => async (dispatch, getState) => {
      const state = getState();

      const CFRReduxState = getCFRState(state);
      const clientCode = getClientCode(state);

      if (CFRReduxState.isCFRReturn) {
        return requests.map(request => {
          if (request.requestName === 'saveSalesDocument') {
            return {
              ...ErplyAttributes.withoutFlatArray(request),
              ...ErplyAttributes.fromFlatArray(request)
                .set('original-invoice-id', `${clientCode}${request.id}`)
                .set(
                  'original-invoice-number',
                  `${clientCode}${request.number}`,
                ).asFlatArray,
            };
          }

          return request;
        });
      }

      return requests;
    },
  },
  onResetShoppingCart: {
    after: () => async dispatch => {
      dispatch(toggleIsOnlineReturn(false));
      dispatch(toggleIsCFRReturn(false));
    },
  },
  onStartNewSale: {
    after: () => async dispatch => {
      dispatch(toggleIsOnlineReturn(false));
      dispatch(toggleIsCFRReturn(false));
    },
  },
};

export default CrossFranchiseReturn;
